import React from "react";
import { CompassParagraph } from "../styledComponents";
import {
  ElementContainer,
  HorizontalLine,
  RowContainer,
} from "../../containers/styledComponents";
import { AIExtractionResponse } from "../../../types/compassAITypes";
import { MESSAGES } from "../../../messages";
import successAnimation from "../../../assets/lottie/success_checkmark_animation.json";
import {
  graphPaletteV2,
  compassDarBlue,
  compassLightBlue,
} from "../../../styles";
import Lottie from "react-lottie-player";
import { IconContainer } from "../../visuals/styledComponents";
import { KPIBox } from "../../visuals/KPIBox";
import { DataEntry } from "../../../models/stewardshipModels";
import calculatorIcon from "../../../assets/svg/metrics_icon.svg";
import { CompactDonutChart } from "../../charts/CompactDonutChart";
import { CompactBarChart } from "../../charts/CompactBarChart";
// =============================================================================
// TYPES
// =============================================================================

export interface ExtractionResultsSummaryProps {
  /** Extraction result data */
  result: AIExtractionResponse;
  /** File name to display */
  fileName?: string;
  /** Show themes distribution (default: true) */
  showThemes?: boolean;
  /** Show milestones distribution (default: true) */
  showMilestones?: boolean;
  /** Compact mode - less spacing */
  compact?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Displays extraction results summary with stats and distributions.
 * Reusable component for showing AI extraction outcomes.
 */
export const ExtractionResultsSummary: React.FC<
  ExtractionResultsSummaryProps
> = ({ result, fileName, showThemes = true, showMilestones = true }) => {
  const summary = result?.summary;
  const messages = MESSAGES.COMPASS_AI.SUMMARY;

  if (!result || !summary) return null;

  // Extract file name from pdf_source URL if fileName not provided
  // URL format contains "f-" prefix before the actual file name
  const extractFileName = (url?: string): string => {
    if (!url) return "document.pdf";
    const parts = url.split("f-");
    return parts.length > 1
      ? parts[parts.length - 1]
      : url.split("/").pop() || url;
  };
  const displayFileName = fileName || extractFileName(result?.pdf_source);

  // Format theme name for display
  const formatThemeName = (theme: string): string => {
    return theme.replace(/_/g, " ");
  };

  return (
    <>
      {/* Main Stats Grid */}
      <RowContainer jc="flex-start" ai="center" ml={-6}>
        <IconContainer size={46} m={1} p={1}>
          <Lottie loop={false} play animationData={successAnimation} />
        </IconContainer>
        <ElementContainer flexDirection="column">
          <CompassParagraph color={compassDarBlue} fontSize={14} bold>
            {messages.EXTRACTION_SUCCESS}
          </CompassParagraph>
          <ElementContainer flexDirection="row">
            <CompassParagraph color={compassDarBlue} mr={5}>
              {messages.FILE_UPLOADED_PREFIX}
            </CompassParagraph>
            <CompassParagraph color={compassLightBlue} underline={true} mr={5}>
              {displayFileName}
            </CompassParagraph>
            <CompassParagraph color={compassDarBlue}>
              {messages.FILE_UPLOADED_SUFFIX}
            </CompassParagraph>
          </ElementContainer>
        </ElementContainer>
      </RowContainer>
      <HorizontalLine marginTop={4} />

      <KPIBox
        icon={calculatorIcon}
        title={messages.EXTRACTION_RESULTS}
        kpis={
          [
            {
              label: messages.ENGAGEMENTS_EXTRACTED,
              value: summary.total_engagements_extracted,
            },
            {
              label: messages.UPDATES_EXTRACTED,
              value: summary.total_updates_extracted,
            },
            {
              label: messages.RELEVANT_PAGES,
              value: summary.relevant_pages_found,
            },
            {
              label: messages.COMPANIES_WITH_ISINS,
              value: summary.companies_with_isins,
            },
          ] as DataEntry<number>[]
        }
      />

      {/* Additional Details */}
      <RowContainer jc="flex-start">
        <ElementContainer flexDirection="row" ai="center" mr={40}>
          <CompassParagraph fontSize={13} mr={8}>
            {messages.PAGES_ANALYZED}:
          </CompassParagraph>
          <CompassParagraph fontSize={13} bold>
            {summary.total_pages_analyzed}
          </CompassParagraph>
        </ElementContainer>
        <ElementContainer flexDirection="row" ai="center">
          <CompassParagraph fontSize={13} mr={8}>
            {messages.PROCESSING_TIME}:
          </CompassParagraph>
          <CompassParagraph fontSize={13} bold>
            {result.processing_time_seconds?.toFixed(1) ?? "—"}s
          </CompassParagraph>
        </ElementContainer>
      </RowContainer>

      {/* Charts Row */}
      <RowContainer jc="flex-start" ai="flex-start" mt={20}>
        {/* Themes Distribution - Donut Chart */}
        {showThemes && Object.keys(summary.themes_distribution).length > 0 && (
          <ElementContainer flexDirection="column" mr={40}>
            <CompassParagraph fontSize={13} bold mb={8}>
              {messages.THEMES_DISTRIBUTION}
            </CompassParagraph>
            <CompactDonutChart
              data={Object.entries(summary.themes_distribution).map(
                ([theme, count]) => ({
                  label: formatThemeName(theme),
                  value: count,
                }),
              )}
              colors={graphPaletteV2}
              size={140}
            />
          </ElementContainer>
        )}

        {/* Milestones Distribution - Bar Chart */}
        {showMilestones &&
          Object.keys(summary.milestones_distribution).length > 0 && (
            <ElementContainer flexDirection="column">
              <CompassParagraph fontSize={13} bold mb={8}>
                {messages.MILESTONES_DISTRIBUTION}
              </CompassParagraph>
              <CompactBarChart
                data={Object.entries(summary.milestones_distribution).map(
                  ([milestone, count]) => ({
                    label: milestone,
                    value: count,
                  }),
                )}
                colors={graphPaletteV2.slice(4)}
                height={140}
                width="100px"
              />
            </ElementContainer>
          )}
      </RowContainer>
    </>
  );
};
